import { defineStore } from 'pinia';

const FUNNEL_ACTIVE_STORAGE_KEY = 'ab-signup-funnel-active';
const FUNNEL_DISMISSED_STORAGE_KEY = 'ab-signup-funnel-dismissed';
const FUNNEL_ACCEPTED_STORAGE_KEY = 'ab-signup-funnel-accepted';
const FUNNEL_LANDING_TRACKED_STORAGE_KEY = 'ab-signup-funnel-landing-tracked';

const canUseSessionStorage = () => typeof window !== 'undefined' && !!window.sessionStorage;

const normalizeQueryValue = (value = '') => {
    const rawValue = Array.isArray(value) ? value[0] : value;
    return `${rawValue || ''}`.trim().toLowerCase();
};

const isEnabledQueryValue = (value = '') => ['1', 'true', 'yes', 'on', 'signup', 'register'].includes(normalizeQueryValue(value));

const getSessionValue = (key = '') => {
    if (!canUseSessionStorage() || !key) {
        return '';
    }
    return `${window.sessionStorage.getItem(key) || ''}`.trim();
};

const setSessionValue = (key = '', value = '') => {
    if (!canUseSessionStorage() || !key) {
        return;
    }
    window.sessionStorage.setItem(key, `${value || ''}`);
};

export const isSignupFunnelQueryEnabled = (value = '') => isEnabledQueryValue(value);
export const isInstantSignupQueryEnabled = (value = '') => isEnabledQueryValue(value);

export const useSignupFunnel = defineStore('signupFunnel', {
    state: () => ({
        enabled: false,
        promptOpen: false,
        dismissed: false,
        accepted: false,
        lastReason: ''
    }),
    getters: {
        canPrompt: (state) => state.enabled && !state.promptOpen && !state.dismissed && !state.accepted
    },
    actions: {
        hydrateFromRoute(route = {}, {
            isLoggedIn = false,
            isPromptSuppressedRoute = false
        } = {}) {
            if (!canUseSessionStorage()) {
                this.enabled = false;
                this.promptOpen = false;
                return {
                    enabled: false,
                    shouldTrackLanding: false
                };
            }

            if (isPromptSuppressedRoute) {
                this.enabled = false;
                this.promptOpen = false;
                return {
                    enabled: false,
                    shouldTrackLanding: false
                };
            }

            const query = route?.query || {};
            const funnelQueryEnabled = isSignupFunnelQueryEnabled(query.funnel);
            const instantSignupEnabled = isInstantSignupQueryEnabled(query.signup);

            if (funnelQueryEnabled && !instantSignupEnabled) {
                setSessionValue(FUNNEL_ACTIVE_STORAGE_KEY, '1');
            }

            this.dismissed = getSessionValue(FUNNEL_DISMISSED_STORAGE_KEY) === '1';
            this.accepted = getSessionValue(FUNNEL_ACCEPTED_STORAGE_KEY) === '1';

            const active = getSessionValue(FUNNEL_ACTIVE_STORAGE_KEY) === '1';
            this.enabled = active && !this.dismissed && !this.accepted && !isLoggedIn && !instantSignupEnabled;

            if (!this.enabled) {
                this.promptOpen = false;
            }

            return {
                enabled: this.enabled,
                shouldTrackLanding: funnelQueryEnabled && !instantSignupEnabled && getSessionValue(FUNNEL_LANDING_TRACKED_STORAGE_KEY) !== '1'
            };
        },
        markLandingTracked() {
            setSessionValue(FUNNEL_LANDING_TRACKED_STORAGE_KEY, '1');
        },
        requestPrompt(reason = 'interaction') {
            if (this.promptOpen) {
                return true;
            }
            if (!this.canPrompt) {
                return false;
            }
            this.lastReason = `${reason || 'interaction'}`.trim().slice(0, 80) || 'interaction';
            this.promptOpen = true;
            return true;
        },
        dismissPrompt() {
            this.dismissed = true;
            this.enabled = false;
            this.promptOpen = false;
            setSessionValue(FUNNEL_DISMISSED_STORAGE_KEY, '1');
        },
        acceptPrompt() {
            this.accepted = true;
            this.enabled = false;
            this.promptOpen = false;
            setSessionValue(FUNNEL_ACCEPTED_STORAGE_KEY, '1');
        }
    }
});
